// services/renova-plus.service.ts
import { Injectable } from '@angular/core';
import { Observable, map, catchError, of } from 'rxjs';
import { ContratoCdcService } from './contrato-cdc.service';
import { ContratoCdc } from '../interfaces/contrato-cdc.model';

export interface RenovaPlusCheck {
  isRenovaPlus: boolean;
  contratosVerificados: number;
  contratoAplicado?: ContratoCdc & { flagRenovacao?: string };
  contratosExibicao?: Array<ContratoCdc & { flagRenovacao?: string }>;
}

@Injectable({
  providedIn: 'root'
})
export class RenovaPlusService {

  constructor(
    private contratoCdcService: ContratoCdcService
  ) {}

  /**
   * Verifica elegibilidade para Renova+ baseado na flag do backend
   * @param cpfCnpj CPF ou CNPJ do cliente
   * @returns Observable com o resultado da verificação
   */
  verificarRenovaPlus(cpfCnpj: string): Observable<RenovaPlusCheck> {
    // Consultar contratos do cliente
    return this.contratoCdcService.listarContratos(cpfCnpj).pipe(
      map((contratos: ContratoCdc[]) => {
        return this.aplicarRegraRenovaPlus(contratos);
      }),
      catchError((error: any) => {
        // Em caso de erro, retorna false (não é Renova+)
        return of({
          isRenovaPlus: false,
          contratosVerificados: 0,
          contratosExibicao: []
        });
      })
    );
  }

  /**
   * Aplica a regra VW-98 para determinar se é Renova+
   * Baseado na flag_renovacao retornada pelo backend
   */
  private aplicarRegraRenovaPlus(contratos: ContratoCdc[]): RenovaPlusCheck {
    const contratosFiltradosOrdenados = this.prepararContratosParaAnalise(contratos);
    const contratosExibicao = this.criarContratosExibicao(contratosFiltradosOrdenados);

    let contratosVerificados = 0;

    for (const contrato of contratosFiltradosOrdenados) {
      contratosVerificados += 1;
      
      // ✅ Verificar apenas a flag do backend
      // A elegibilidade (data) já foi validada no backend
      if ((contrato as any).flag_renovacao === 'S' || 
          (contrato as any).flRenovacao === 'S') {
        return {
          isRenovaPlus: true,
          contratosVerificados,
          contratoAplicado: contrato,
          contratosExibicao
        };
      }
    }

    // Nenhum contrato atende à regra
    return {
      isRenovaPlus: false,
      contratosVerificados,
      contratosExibicao
    };
  }

  /**
   * Calcula a data fim do período vigente: dataInicio + prazo + 6 meses
   */
  private prepararContratosParaAnalise(contratos: ContratoCdc[]): ContratoCdc[] {
    return contratos
      .filter((contrato) => this.isContratoConsiderado(contrato))
      .sort((a, b) => this.ordenarPorDataInicioDesc(a.dataInicio, b.dataInicio));
  }

  private isContratoConsiderado(contrato: ContratoCdc): boolean {
    const chassiValido = (contrato.chassi || '').trim().length > 0;
    const tipoFinanciamento = (contrato.tipoFinanciamento || '').trim().toUpperCase();
    const ehCreditoPessoal = tipoFinanciamento === 'CRÉDITO PESSOAL' || tipoFinanciamento === 'CREDITO PESSOAL';

    return chassiValido && !ehCreditoPessoal && !!contrato.dataInicio && contrato.prazo !== undefined && contrato.prazo !== null;
  }

  private ordenarPorDataInicioDesc(dataInicioA: string, dataInicioB: string): number {
    const dateA = new Date(dataInicioA);
    const dateB = new Date(dataInicioB);
    if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
      // Se uma data é inválida, coloca no final
      if (isNaN(dateA.getTime())) return 1;
      if (isNaN(dateB.getTime())) return -1;
      return 0;
    }
    return dateB.getTime() - dateA.getTime();
  }

  private criarContratosExibicao(contratos: ContratoCdc[]): Array<ContratoCdc & { flagRenovacao?: string }> {
    return contratos.map((contrato) => ({
      ...contrato,
      flagRenovacao: (contrato as any).flag_renovacao || (contrato as any).flRenovacao || 'N'
    }));
  }
}